import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { StaffService } from '../api/services/staffService';

export default function RecordAttendanceModal({ open, onClose, onSuccess, staffMembers }) {
    const defaultState = {
        staffId: null,
        date: new Date().toISOString().split('T')[0],
        status: 'Present',
        clockInTime: '',
        clockOutTime: ''
    };

    const [formData, setFormData] = useState(defaultState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (open) {
            setFormData(defaultState);
            setErrors({});
            setSubmitError('');
        }
    }, [open]);

    const validate = () => {
        const newErrors = {};
        if (!formData.staffId) newErrors.staffId = 'Staff member is required';
        if (!formData.date) newErrors.date = 'Date is required';
        if (!formData.status) newErrors.status = 'Status is required';
        
        // Clock in time might be required if status is Present or Late
        if (['Present', 'Late', 'Half-Day'].includes(formData.status) && !formData.clockInTime) {
            newErrors.clockInTime = 'Clock in time is required for this status';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const payload = {
                staffId: formData.staffId.id || formData.staffId._id,
                date: formData.date,
                status: formData.status,
            };

            if (formData.clockInTime) {
                // Convert local datetime string to ISO 8601 with Z
                payload.clockInTime = new Date(formData.clockInTime).toISOString();
            }

            if (formData.clockOutTime) {
                payload.clockOutTime = new Date(formData.clockOutTime).toISOString();
            }

            await StaffService.recordAttendance(payload);
            onSuccess('Attendance recorded successfully');
            onClose();
        } catch (error) {
            setSubmitError(error.message || 'Failed to record attendance. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Record Attendance</DialogTitle>
            <DialogContent dividers>
                {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                    <Autocomplete
                        options={(staffMembers || []).filter(s => (s.status || 'Active').toLowerCase() === 'active')}
                        getOptionLabel={(option) => option.fullName || option.staffName || option.name || 'Unknown'}
                        value={formData.staffId}
                        onChange={(e, newValue) => setFormData({ ...formData, staffId: newValue })}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Staff Member" 
                                error={!!errors.staffId}
                                helperText={errors.staffId}
                                required
                            />
                        )}
                        disabled={isSubmitting}
                    />

                    <TextField
                        type="date"
                        label="Date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.date}
                        helperText={errors.date}
                        required
                        disabled={isSubmitting}
                    />

                    <FormControl required error={!!errors.status} disabled={isSubmitting}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={formData.status}
                            label="Status"
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                            <MenuItem value="Present">Present</MenuItem>
                            <MenuItem value="Absent">Absent</MenuItem>
                            <MenuItem value="Late">Late</MenuItem>
                            <MenuItem value="Half-Day">Half-Day</MenuItem>
                        </Select>
                    </FormControl>

                    {['Present', 'Late', 'Half-Day'].includes(formData.status) && (
                        <>
                            <TextField
                                type="datetime-local"
                                label="Clock In Time"
                                value={formData.clockInTime}
                                onChange={(e) => setFormData({ ...formData, clockInTime: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.clockInTime}
                                helperText={errors.clockInTime}
                                required
                                disabled={isSubmitting}
                            />
                            <TextField
                                type="datetime-local"
                                label="Clock Out Time"
                                value={formData.clockOutTime}
                                onChange={(e) => setFormData({ ...formData, clockOutTime: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.clockOutTime}
                                helperText={errors.clockOutTime}
                                disabled={isSubmitting}
                            />
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit} 
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Recording...' : 'Record Attendance'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
